/**
 * THE CONSENT PIN
 *
 * `CONSENT_TEXT` is the exact sentence rendered beside the checkbox on step 2.
 * `CONSENT_VERSION` is recorded on every lead, so a record in the CRM can be traced
 * back to the words the customer actually saw.
 *
 * `CONSENT_TEXT_SHA256` is what makes that traceability real rather than
 * aspirational. But a hash stored beside the value it hashes only proves internal
 * consistency at HEAD; on its own it cannot tell you the version string was not left
 * stale. So `CONSENT_VERSION` is not a free-standing label, it is BUILT from the
 * hash: `<effective date>.<first 8 hex chars of CONSENT_TEXT_SHA256>`. Editing the
 * text and recomputing the digest without updating that suffix fails
 * `functions/src/consentPin.test.ts`, which is what turns a stale version into a CI
 * failure instead of a silent lie in the CRM. The date segment may change alone, so
 * re-dating an unchanged sentence still works.
 *
 * To change the consent language, in ONE commit:
 *   1. Edit `CONSENT_TEXT`.
 *   2. Recompute the digest:
 *      node -e 'const{createHash}=require("crypto");console.log(createHash("sha256").update(process.argv[1],"utf8").digest("hex"))' "<the new text>"
 *   3. Set `CONSENT_TEXT_SHA256` to the full digest.
 *   4. Set `CONSENT_VERSION` to `<today>.<first 8 hex of that digest>`.
 *   5. Close the outgoing `CONSENT_HISTORY` entry with an `effectiveTo` and prepend
 *      the new one.
 *
 * WHERE THIS SENTENCE CAME FROM, AND THE LIMIT ON THAT.
 *
 * It is reproduced VERBATIM from `dac-qualifier`, where it was drafted and reviewed
 * as prior express WRITTEN consent (PEWC) under 47 CFR 64.1200(f)(9). That is a
 * materially different thing from prior express consent: PEWC is what covers
 * marketing calls and texts placed with an autodialer or an artificial, prerecorded
 * or AI voice, which is what a CRM sequence actually sends. Three elements must
 * survive any future edit, and `consentPin.test.ts` asserts all three:
 *
 *   1. Authorisation of automated technology and prerecorded messages.
 *   2. "Consent is not a condition of any purchase."
 *   3. A stated opt-out (STOP).
 *
 * **The legal record lives in the OTHER repo**, at
 * `dac-qualifier/docs/legal-review-2026-08-05.md`, question 2, Option B. It is cited
 * across repos on purpose rather than copied, because it is Nico's memo about
 * dac-qualifier and pretending otherwise would be the inherited-citation mistake this
 * project has made before. **Nico has not reviewed this text in Studio's context.**
 * The reasoning transfers on its face: the sentence names no product, Studio's leads
 * reach the same n8n instance and the same automated sequences, and the Vapi
 * receptionist that motivated the PEWC upgrade is the same project. Element 2 also
 * still holds literally, because Studio's result is free and states no price at all
 * (ADR-0016). Confirming that is a question for Nico, not an inference to rely on.
 */
export const CONSENT_TEXT =
  'By checking this box, I agree that Kasselman Solar may call, text, and email me ' +
  'about my solar project and related Kasselman Solar products and services, at the ' +
  'number and email I provided, including calls and texts made with automated ' +
  'technology or prerecorded messages. Consent is not a condition of any purchase. ' +
  'Message and data rates may apply; reply STOP to any text to opt out.';

/**
 * SHA-256 of `CONSENT_TEXT`, full digest.
 *
 * Not decoration: `CONSENT_VERSION` is derived from its first eight characters, so
 * editing the sentence and recomputing this without updating the version fails the
 * pin test. A hash beside the value it hashes proves consistency at HEAD; deriving
 * the version from it gives that consistency a memory.
 */
export const CONSENT_TEXT_SHA256 =
  'f4e451c5ce2f04daff358e06b14dc0c3e1bc7f5b94aafeeaafef5e697656ee2e';

/**
 * What a lead records as the wording the customer agreed to.
 *
 * Shape is `<effective date>.<first 8 hex of CONSENT_TEXT_SHA256>`. The digest half
 * matches dac-qualifier's because the sentence is byte-identical; the date is
 * Studio's own, being the day the wording was adopted here. **No customer has been
 * shown it yet**, since the funnel is not live.
 *
 * **The server must NEVER validate this against its own constant.** `web` and
 * `functions` deploy on different cadences (ADR-0027 for the branch split), so
 * between a deploy and a promotion the two legitimately disagree about which version
 * is current, and an equality check would reject every real lead in that window. The
 * client is the only accurate witness to what was rendered on screen.
 */
export const CONSENT_VERSION = '2026-09-22.f4e451c5'; // date . first 8 of CONSENT_TEXT_SHA256

/** One consent wording, with the exact text shown while it was live. */
export type ConsentRecord = {
  readonly version: string;
  readonly text: string;
  readonly sha256: string;
  /** ISO date this wording began being shown. */
  readonly effectiveFrom: string;
  /** ISO date it stopped, or null for the wording live right now. */
  readonly effectiveTo: string | null;
};

/**
 * Every consent wording Studio has ever shown, newest first, with the exact text.
 *
 * WHY A HASH IS NOT ENOUGH. `CONSENT_TEXT_SHA256` proves integrity only for the text
 * sitting next to it. The moment the sentence changes, every lead already in the CRM
 * carries a `consentVersion` naming a sentence that exists nowhere in the running
 * system, and reconstructing it means git archaeology against a deployment date. Two
 * independent reasons that is not good enough:
 *
 *   1. The amended FTC Telemarketing Sales Rule (16 CFR 310.5, in force since
 *      October 2024) requires sellers to retain a record of each consumer's express
 *      consent for five years.
 *   2. In TCPA litigation the burden of proving consent is ours, and what must be
 *      produced is the disclosure AS THE CONSUMER SAW IT. A digest proves integrity
 *      only if the text it digests can still be produced.
 *
 * WHAT THIS IS NOT. This is the WORDING archive, not the consent archive. The
 * per-lead facts (which version, which timestamp, the number and email given) live in
 * the CRM, because Studio keeps no datastore. Both halves are needed to answer "what
 * did this person agree to, and when". Retiring a wording here does NOT permit
 * deleting the leads captured under it.
 *
 * The live wording is always index 0 and always the only entry with a null
 * `effectiveTo`. `consentPin.test.ts` enforces both, and every entry's digest, so a
 * hand-edited archive fails CI rather than quietly becoming fiction.
 */
export const CONSENT_HISTORY: readonly ConsentRecord[] = Object.freeze([
  Object.freeze({
    version: CONSENT_VERSION,
    text: CONSENT_TEXT,
    sha256: CONSENT_TEXT_SHA256,
    effectiveFrom: '2026-09-22',
    effectiveTo: null,
  }),
]);

/**
 * The wording a recorded `consentVersion` refers to, or undefined if the archive has
 * no such version.
 *
 * Undefined rather than a throw or a fallback string: this is what answers a
 * discovery request or a complaint, and inventing plausible text for an unknown
 * version is the one behaviour that would make the archive worse than useless.
 */
export function consentTextForVersion(version: string): string | undefined {
  return CONSENT_HISTORY.find((r) => r.version === version)?.text;
}

/**
 * The consent record carried on the request and forwarded on the lead.
 *
 * `contactConsent` is the LITERAL `true`, not `boolean`, because there is no such
 * thing as a lead carrying a false consent: the server rejects the request instead.
 * Typing it `boolean` would make the impossible state representable and invite a
 * downstream reader to branch on it.
 */
export type Consent = {
  contactConsent: true;
  /**
   * ISO 8601, set by the CLIENT at the moment the box was ticked.
   *
   * Not the server's receipt time, which is a different fact. The two differ by a
   * network hop normally and by the whole retry budget when a handoff is retried, and
   * what a consent record has to state is when the customer consented.
   */
  contactConsentAt: string;
  /**
   * The `CONSENT_VERSION` the CLIENT rendered, never the server's own.
   *
   * @see CONSENT_VERSION - why the server must not validate this
   */
  consentVersion: string;
};
